import { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import LoadingSpinner from '../components/LoadingSpinner';
import { safeReturnTo } from '../lib/returnTo';

export default function Login() {
  const { t, i18n } = useTranslation();
  const { login, completeNewPassword, sessionExpired } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  // Where to go after login: a validated same-site path only (open-redirect guard).
  const returnTo = safeReturnTo(searchParams.get('next')) ?? '/';

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  // New password flow
  const [needsNewPassword, setNeedsNewPassword] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const result = await login(email, password);
      if (result.needsNewPassword) {
        setNeedsNewPassword(true);
      } else {
        navigate(returnTo, { replace: true });
      }
    } catch (err: any) {
      const msg = err?.message || err?.name || '';
      if (msg.includes('attempts exceeded') || msg.includes('LimitExceeded')) {
        setError(t('auth.tooManyAttempts'));
      } else if (msg.includes('Temporary password') || msg.includes('ExpiredTemporaryPassword')) {
        setError(t('auth.expiredTempPassword'));
      } else if (msg.includes('not confirmed') || msg.includes('UserNotConfirmedException')) {
        setError(t('auth.accountNotConfirmed'));
      } else {
        setError(t('auth.invalidCredentials'));
      }
    } finally {
      setLoading(false);
    }
  }

  async function handleNewPassword(e: React.FormEvent) {
    e.preventDefault();
    setError('');

    if (newPassword !== confirmPassword) {
      setError(t('validation.passwordMismatch'));
      return;
    }

    if (newPassword.length < 8 || !/[A-Z]/.test(newPassword) || !/[a-z]/.test(newPassword) || !/[0-9]/.test(newPassword)) {
      setError(t('validation.passwordRequirements'));
      return;
    }

    setLoading(true);
    try {
      await completeNewPassword(newPassword);
      navigate(returnTo, { replace: true });
    } catch (err: any) {
      const msg = err?.message || err?.name || '';
      if (msg.includes('attempts exceeded') || msg.includes('LimitExceeded')) {
        setError(t('auth.tooManyAttempts'));
      } else {
        setError(t('auth.passwordSetFailed'));
      }
    } finally {
      setLoading(false);
    }
  }

  function toggleLanguage() {
    const newLang = i18n.language === 'en' ? 'fr' : 'en';
    i18n.changeLanguage(newLang);
  }

  return (
    <div className="min-h-screen flex flex-col bg-slate-50">
      {/* Header */}
      <header className="w-full py-6 bg-header">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex justify-between items-center">
          <img src="/logo.png?v=2" alt="Arivo" className="h-[60px] w-auto" />
          <button type="button"
            onClick={toggleLanguage}
            className="px-3 py-1.5 text-sm font-medium text-white/80 hover:text-white hover:bg-white/10 rounded-lg"
          >
            {i18n.language === 'en' ? 'FR' : 'EN'}
          </button>
        </div>
      </header>

      <div className="flex-1 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
        <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <div className="bg-white py-8 px-4 sm:rounded-xl sm:px-10 border border-slate-200">
          {needsNewPassword ? (
            // New password form
            <form className="space-y-6" onSubmit={handleNewPassword}>
              <div className="text-center mb-4">
                <h2 className="text-lg font-semibold text-slate-900">{t('auth.setNewPassword')}</h2>
                <p className="text-sm text-slate-600 mt-1">{t('auth.setNewPasswordDesc')}</p>
              </div>

              {error && (
                <div className="bg-danger-50 border border-danger-200 text-danger-700 px-4 py-3 rounded-lg text-sm">
                  {error}
                </div>
              )}

              <div>
                <label htmlFor="newPassword" className="label">
                  {t('auth.newPassword')}
                </label>
                <input
                  id="newPassword"
                  name="newPassword"
                  type="password"
                  autoComplete="new-password"
                  required
                  value={newPassword}
                  onChange={e => setNewPassword(e.target.value)}
                  className="input"
                  disabled={loading}
                />
              </div>

              <div>
                <label htmlFor="confirmPassword" className="label">
                  {t('auth.confirmPassword')}
                </label>
                <input
                  id="confirmPassword"
                  name="confirmPassword"
                  type="password"
                  autoComplete="new-password"
                  required
                  value={confirmPassword}
                  onChange={e => setConfirmPassword(e.target.value)}
                  className="input"
                  disabled={loading}
                />
              </div>

              <p className="text-xs text-slate-500">{t('validation.passwordRequirements')}</p>

              <div>
                <button
                  type="submit"
                  disabled={loading}
                  className="w-full btn-primary py-2.5"
                >
                  {loading ? <LoadingSpinner size="sm" className="text-white" /> : t('auth.setPassword')}
                </button>
              </div>
            </form>
          ) : (
            // Login form
            <form className="space-y-6" onSubmit={handleSubmit}>
              {sessionExpired && !error && (
                <div className="bg-amber-50 border border-amber-200 text-amber-800 px-4 py-3 rounded-lg text-sm">
                  {t('auth.sessionExpired')}
                </div>
              )}
              {error && (
                <div className="bg-danger-50 border border-danger-200 text-danger-700 px-4 py-3 rounded-lg text-sm">
                  {error}
                </div>
              )}

              <div>
                <label htmlFor="email" className="label">
                  {t('auth.email')}
                </label>
                <input
                  id="email"
                  name="email"
                  type="email"
                  autoComplete="email"
                  required
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                  className="input"
                  disabled={loading}
                />
              </div>

              <div>
                <label htmlFor="password" className="label">
                  {t('auth.password')}
                </label>
                <input
                  id="password"
                  name="password"
                  type="password"
                  autoComplete="current-password"
                  required
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  className="input"
                  disabled={loading}
                />
              </div>

              <div className="flex items-center justify-end">
                <Link
                  to="/forgot-password"
                  className="text-sm text-primary-600 hover:text-primary-500"
                >
                  {t('auth.forgotPassword')}
                </Link>
              </div>

              <div>
                <button
                  type="submit"
                  disabled={loading}
                  className="w-full btn-primary py-2.5"
                >
                  {loading ? <LoadingSpinner size="sm" className="text-white" /> : t('auth.login')}
                </button>
              </div>
            </form>
          )}
        </div>
        </div>
      </div>
    </div>
  );
}
